import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type FeatureValue = string | boolean | null;
type PlanId = 'free' | 'monthly' | 'quarterly' | 'halfyearly';

export function usePricingPageContent() {
  const { t } = useTranslation();

  const plans = useMemo(
    () =>
      [
        {
          id: 'free' as PlanId,
          name: t('views.pricing.planFree'),
          description: t('views.pricing.planFreeDescJobSearch', {
            defaultValue: 'Try job discovery and resume tailoring at your own pace',
          }),
          priceLabel: t('views.pricing.freeForever'),
          packId: null,
          periodLabel: '',
          effectiveLabel: null,
          credits: 3,
          totalCredits: t('views.pricing.lifetimeOptimizations', { count: 3 }),
          badge: null,
          popular: false,
          autoRenew: false,
          buttonText: t('views.pricing.startForFree'),
          savePct: null,
        },
        {
          id: 'monthly' as PlanId,
          name: t('views.pricing.planMonthly'),
          description: t('views.pricing.planMonthlyDesc'),
          priceLabel: null,
          packId: 'pack-monthly',
          periodLabel: t('views.pricing.perMonthSlash', { defaultValue: '/ month' }),
          effectiveLabel: null,
          credits: 60,
          totalCredits: t('views.pricing.optimizationsPerMonth', {
            count: 60,
            defaultValue: '60 optimizations / month',
          }),
          badge: { text: t('views.pricing.mostPopular'), color: 'bg-[var(--vscode-primary)] text-white' },
          popular: true,
          autoRenew: true,
          buttonText: t('views.pricing.subscribeMonthly'),
          savePct: null,
        },
        {
          id: 'quarterly' as PlanId,
          name: t('views.pricing.planQuarterly'),
          description: t('views.pricing.planQuarterlyDesc'),
          priceLabel: null,
          packId: 'pack-quarterly',
          periodLabel: t('views.pricing.per3Months'),
          effectiveLabel: t('views.pricing.effectiveMonthly', {
            amount: '₹250',
            defaultValue: '₹250 / month',
          }),
          credits: 60,
          totalCredits: t('views.pricing.optimizationsTotal', {
            count: 180,
            defaultValue: '180 optimizations total',
          }),
          badge: {
            text: t('views.pricing.badgeBestValue', { defaultValue: 'Best Value' }),
            color: 'bg-emerald-500 text-white',
          },
          popular: false,
          autoRenew: true,
          buttonText: t('views.pricing.subscribeQuarterly'),
          savePct: t('views.pricing.savePct', { pct: '28', defaultValue: 'Save 28%' }),
        },
        {
          id: 'halfyearly' as PlanId,
          name: t('views.pricing.planHalfYearly'),
          description: t('views.pricing.planHalfYearlyDesc'),
          priceLabel: null,
          packId: 'pack-halfyearly',
          periodLabel: t('views.pricing.per6Months'),
          effectiveLabel: t('views.pricing.effectiveMonthly', {
            amount: '₹250',
            defaultValue: '₹250 / month',
          }),
          credits: 60,
          totalCredits: t('views.pricing.optimizationsTotal', {
            count: 360,
            defaultValue: '360 optimizations total',
          }),
          badge: {
            text: t('views.pricing.badgeMaxSavings', { defaultValue: 'Max Savings' }),
            color: 'bg-amber-500 text-white',
          },
          popular: false,
          autoRenew: true,
          buttonText: t('views.pricing.subscribeHalfYearly'),
          savePct: t('views.pricing.savePct', { pct: '28', defaultValue: 'Save 28%' }),
        },
      ] as const,
    [t],
  );

  const planFeatures = useMemo(
    () =>
      [
        {
          label: t('views.pricing.featureOptimizations'),
          free: t('views.pricing.value3Lifetime'),
          monthly: t('views.pricing.value60Month'),
          quarterly: t('views.pricing.value180Quarter'),
          halfyearly: t('views.pricing.value360HalfYear'),
        },
        {
          label: t('views.pricing.featureAiModel'),
          free: t('views.pricing.valueFastAI'),
          monthly: t('views.pricing.valuePremiumAI'),
          quarterly: t('views.pricing.valuePremiumAI'),
          halfyearly: t('views.pricing.valuePremiumAI'),
        },
        {
          label: t('views.pricing.featurePdfDownload'),
          free: false,
          monthly: true,
          quarterly: true,
          halfyearly: true,
        },
        {
          label: t('views.pricing.featureCoverLetter', { defaultValue: 'Cover letter AI' }),
          free: false,
          monthly: true,
          quarterly: true,
          halfyearly: true,
        },
        {
          label: t('views.pricing.featureApplicationTracking', { defaultValue: 'Application tracking' }),
          free: t('views.pricing.value3TrackerEntries', { defaultValue: '3 entries' }),
          monthly: t('views.pricing.valueUnlimited'),
          quarterly: t('views.pricing.valueUnlimited'),
          halfyearly: t('views.pricing.valueUnlimited'),
        },
        {
          label: t('views.pricing.featureOptimizationHistory'),
          free: false,
          monthly: t('views.pricing.value30Days'),
          quarterly: t('views.pricing.valueFullHistory'),
          halfyearly: t('views.pricing.valueFullHistory'),
        },
        {
          label: t('views.pricing.featureCalendarReminders'),
          free: false,
          monthly: true,
          quarterly: true,
          halfyearly: true,
        },
        {
          label: t('views.pricing.featurePriorityProcessing'),
          free: false,
          monthly: true,
          quarterly: true,
          halfyearly: true,
        },
        {
          label: t('views.pricing.featureAutoRenews'),
          free: null,
          monthly: true,
          quarterly: true,
          halfyearly: true,
        },
        {
          label: t('views.pricing.cancelAnytime'),
          free: null,
          monthly: true,
          quarterly: true,
          halfyearly: true,
        },
      ] as {
        label: string;
        free: FeatureValue;
        monthly: FeatureValue;
        quarterly: FeatureValue;
        halfyearly: FeatureValue;
      }[],
    [t],
  );

  const competitors = useMemo(
    () => [
      t('views.pricing.competitorGgj', { defaultValue: 'GoGrabJob' }),
      'Jobscan',
      'Rezi',
      'Kickresume',
      'Huntr',
      'TrackJobs',
    ],
    [t],
  );

  const compareRows = useMemo(
    () =>
      [
        {
          label: t('views.pricing.compareMonthlyPrice', { defaultValue: 'Monthly price' }),
          values: ['₹349', '₹4,195', '₹2,436', '₹2,016', '₹3,360', '₹504'],
        },
        {
          label: t('views.pricing.compareLongTermPlan', { defaultValue: 'Quarterly / long-term plan' }),
          values: [true, false, false, true, false, false],
        },
        {
          label: t('views.pricing.compareAiOptimization', { defaultValue: 'AI resume optimization' }),
          values: [true, true, true, true, false, false],
        },
        {
          label: t('views.pricing.compareAtsScore', { defaultValue: 'ATS compatibility score' }),
          values: [true, true, true, true, false, false],
        },
        {
          label: t('views.pricing.compareCoverLetter', { defaultValue: 'AI cover letter' }),
          values: [true, false, true, true, false, false],
        },
        {
          label: t('views.pricing.compareJobTracker', { defaultValue: 'Job application tracker' }),
          values: [true, false, false, false, true, true],
        },
        {
          label: t('views.pricing.compareInrPricing', { defaultValue: 'India native pricing (INR)' }),
          values: [true, false, false, false, false, false],
        },
        {
          label: t('views.pricing.compareFreeTier', { defaultValue: 'Free tier available' }),
          values: [true, t('views.pricing.compare3ScansMo', { defaultValue: '3 scans/mo' }), false, t('views.pricing.compareLimited', { defaultValue: 'Limited' }), true, false],
        },
        {
          label: t('views.pricing.featurePriorityProcessing'),
          values: [true, false, false, false, false, false],
        },
        {
          label: t('views.pricing.cancelAnytime'),
          values: [true, true, true, true, true, true],
        },
      ] as { label: string; values: FeatureValue[] }[],
    [t],
  );

  const priceSavings = useMemo(
    () => [
      '',
      t('views.pricing.saving12x', { defaultValue: '12× cheaper' }),
      t('views.pricing.saving7x', { defaultValue: '7× cheaper' }),
      t('views.pricing.saving6x', { defaultValue: '6× cheaper' }),
      t('views.pricing.saving10x', { defaultValue: '10× cheaper' }),
      t('views.pricing.saving14x', { defaultValue: '1.4× cheaper' }),
    ],
    [t],
  );

  const copy = useMemo(
    () => ({
      heroTitle: t('views.pricing.headline'),
      heroSubtitle: t('views.pricing.heroSubtitleLong', {
        defaultValue:
          'Start free. Upgrade to a plan that fits your job search timeline — and save up to 28% with longer commitments.',
      }),
      oneMinuteSetup: t('views.pricing.oneMinuteSetup'),
      freeToStart: t('views.pricing.freeToStart'),
      cancelAnytime: t('views.pricing.cancelAnytime'),
      indiaNativePricing: t('views.pricing.indiaNativePricing', { defaultValue: 'India-native pricing' }),
      whatsIncluded: t('views.pricing.whatsIncluded'),
      autoRenew: t('views.pricing.autoRenew'),
      noCreditCard: t('views.pricing.noCreditCardRequired'),
      effectiveLabel: t('views.pricing.effectiveApprox', { defaultValue: 'effective' }),
      saveVsMonthly: t('views.pricing.saveVsMonthly', { defaultValue: 'vs monthly' }),
      loading: t('common.loading', { defaultValue: 'Loading…' }),
      comparePlansTitle: t('views.pricing.comparePlansTitle', { defaultValue: "What's in every plan" }),
      comparePlansSubtitle: t('views.pricing.comparePlansSubtitle', {
        defaultValue: 'Detailed breakdown of every feature across all GoGrabJob plans.',
      }),
      featureColumn: t('views.pricing.featureColumn', { defaultValue: 'Feature' }),
      competitorTitle: t('views.pricing.competitorTitle', { defaultValue: 'How we compare to the competition' }),
      competitorSubtitle: t('views.pricing.competitorSubtitle', {
        defaultValue:
          'Full-loop job search — discovery, resume tailoring, and ATS scoring — at a fraction of competitor pricing, built for the Indian market.',
      }),
      footerInr: t('views.pricing.footerInr'),
      contactUs: t('views.pricing.contactUs'),
      forCustomNeeds: t('views.pricing.forCustomNeeds'),
      competitorFootnote: t('views.pricing.competitorFootnote', {
        defaultValue: 'Competitor prices in INR equivalent (Feb 2026). Verified from public pricing pages.',
      }),
      positioningTitle: t('views.pricing.positioningTitle', {
        defaultValue: 'Serious job-search AI for the price of a coffee',
      }),
      positioningBody: t('views.pricing.positioningBody', {
        defaultValue:
          'Auto-renews monthly, quarterly, or half-yearly — cancel anytime from your dashboard. India-native pricing, no USD conversion surprises.',
      }),
    }),
    [t],
  );

  return { plans, planFeatures, competitors, compareRows, priceSavings, copy };
}

export type { FeatureValue, PlanId };
